
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Vue, Options } from "vue-class-component";
import HeaderUpdate from "./header-update.vue";
import NotFoundState from "./components/NotFoundState.vue";
import SearchFilter from "./components/SearchFilter.vue";
import SummaryInfo from "./components/SummaryInfo.vue";
import { ResponsePayloadV2 } from "@/domain/entities/ResponsePayload";
import {
  ApiRequestList,
  ErrorMessageEntities,
  ModalMessageEntities,
  OptionsClass
} from "@/domain/entities/MainApp";
import { DispatchTemporary } from "@/domain/entities/Dispatch";
import { DispatchController } from "@/app/ui/controllers/DispatchController";
import {
  formatValueNumber,
  parsingErrorResponse,
  removeDataLocalStorage
} from "@/app/infrastructures/misc/Utils";
import { getHubData } from "@/app/infrastructures/misc/Cookies";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import EmptyList from "@/app/ui/components/empty-list/index.vue";
import { handleCommonErrors } from "@/app/infrastructures/misc/ErrorHandler";
import EmptyState from "./components/EmptyState.vue";
import { CargoScanFailed } from "@/domain/entities/Cargo";
import { CategorizedItem } from "@/app/ui/components/categorized-selector/index.vue";
import { ProcessUpdateDispatchRequest } from "@/data/payload/api/DispatchApiRequest";

@Options({
  components: {
    HeaderUpdate,
    NotFoundState,
    SearchFilter,
    SummaryInfo,
    EmptyList,
    EmptyState
  }
})
export default class UpdateDispatch extends Vue {
  mounted() {
    this.getList();
  }

  goBack() {
    this.errorEntities.onClose && this.errorEntities.onClose();
    MainAppController.closeErrorMessage();
    MainAppController.closeLoading();
    MainAppController.closeMessageModal();
    this.$router.push("/dispatch");
  }
  get errorEntities() {
    return MainAppController.errorMessageEntities;
  }

  form = new ApiRequestList({
    hubId: getHubData().hubId || "0",
    q: "",
    dtScanStatus: new OptionsClass(),
    isError: false,
    errorData: new ErrorMessageEntities()
  });
  temporaryList = new ResponsePayloadV2();
  lastScanData = new DispatchTemporary();

  get demStatus () {
    const demNo = this.lastScanData?.dtDemNo;
    if (!demNo) return this.lastScanData?.dtDemStatus;
    const sameDemItems: DispatchTemporary[] = (this.temporaryList.data || []).filter(
      (item: DispatchTemporary) => item.dtDemNo === demNo
    );
    if (sameDemItems.length === 0) return false;
    return sameDemItems.every((item: DispatchTemporary) => item.dtScanStatus === "success");
  }

  async getList() {
    try {
      this.temporaryList.loading = true;
      this.form.error = false;
      this.form.errorData = new ErrorMessageEntities();

      const { errorData, dtScanStatus, ...params } = this.form;
      this.temporaryList = await DispatchController.getListTemporary(
        new ApiRequestList({
          ...params,
          dtScanStatus: dtScanStatus?.value?.replace(/^all$/, ""),
          isPagination: false
        })
      );

      if (!this.form.q && !this.form.dtScanStatus?.value) {
        this.lastScanData = this.temporaryList.data[0];
      }
    } catch (error) {
      this.form.error = true;
      this.form.errorData = handleCommonErrors({ error });
    } finally {
      this.temporaryList.loading = false;
    }
  }

  private toCargoScanFailed(item: DispatchTemporary): CargoScanFailed {
    return new CargoScanFailed({
      sttNumber: item.dtSttNo,
      lastStatus: item.sttLastStatusId ?? "",
      failureReason: item.sttErrorMessage ?? "",
      demNo: item.dtDemNo,
      success: false,
    });
  }

  get listSttFailed(): CargoScanFailed[] {
    const data: DispatchTemporary[] = this.temporaryList.data || [];
    return data
      .filter((item) => item.dtScanStatus.includes("failed"))
      .map(this.toCargoScanFailed);
  }

  get listSttSuccess(): DispatchTemporary[] {
    const data: DispatchTemporary[] = this.temporaryList.data || [];
    return data.filter((item) => item.dtScanStatus === "success");
  }

  get summaryData() {
    const data: DispatchTemporary[] = this.temporaryList.data;
    const successItems = data?.filter(
      (item: DispatchTemporary) => item.dtScanStatus === "success"
    );

    return {
      totalStt: successItems.length,
      totalPieces: successItems.reduce(
        (acc, item) => acc + (item.dtPieces || 0),
        0
      ),
      totalWeight: formatValueNumber(
        successItems.reduce(
          (acc, item) => acc + (item.dtChargeableWeight || 0),
          0
        )
      )
    };
  }

  get columns() {
    return [
      ...this.columnsFirstSection,
      ...this.columnSecondSection,
      ...this.columnThirdSection
    ];
  }

  get columnsFirstSection() {
    return [
      {
        name: "No.",
        styleHead: "text-left w-12",
        render: (_: DispatchTemporary, index: number) => {
          return `<div class="text-sm text-black-lp-300 text-left ${
            index === 0 ? "highlight" : ""
          }">${this.listSttSuccess?.length - index}</div>`;
        }
      },
      {
        name: "No. STT",
        styleHead: "text-left w-64",
        render: (item: DispatchTemporary, index: number) =>
          `<div class="text-sm text-black-lp-300 text-left flex flex-col gap-y-1 ${index === 0 ? "font-semibold" : "font-normal"}">
            ${index === 0 ? this.getStyledSttNo(item.dtSttNo) : item.dtSttNo}
            <div class="text-black-lp-300 flex font-normal"><div class="rounded px-2 py-0 bg-gray-lp-400">
              ${item.dtProductType}
            </div></div>
            ${
              item.dtScanStatus !== "success"
                ? `<br /><div class="w-max text-sm font-medium text-left px-4 py-1 rounded-full bg-red-lp-600 text-red-lp-300">
                  ${item.sttErrorMessage}
                </div>`
                : ""
            }
          </div>`
      },
      {
        name: this.$t("dispatch.referenceNo"),
        styleHead: "text-left w-40",
        render: (item: DispatchTemporary) => {
          return `<div class="text-sm whitespace-no-wrap break-all break-words text-black-lp-300 text-left">
              ${item.dtRefNo || "-"}
            </div>`;
        }
      }
    ];
  }

  get columnSecondSection() {
    return [
      {
        name: this.$t("dispatch.totalPieces"),
        styleHead: "text-left w-32",
        render: (item: DispatchTemporary) =>
          `<div class="text-sm text-black-lp-300 text-left">
            ${item.dtPieces}
          </div>`
      },
      {
        name: "Ursa code",
        styleHead: "text-left w-32",
        render: (item: DispatchTemporary) => {
          return `<div class="text-sm text-black-lp-300 text-left">
            ${item.dtDestinationDistrictUrsaCode}
            </div>`;
        }
      }
    ];
  }

  get columnThirdSection() {
    return [
      {
        name: "No. DEM",
        styleHead: "text-left w-40",
        render: (item: DispatchTemporary) =>
          `<div class="text-sm text-black-lp-300 text-left">
            ${item.dtDemNo || "-"}
          </div>`
      }
    ];
  }

  async updateScan(data: DispatchTemporary[]) {
    let updatedData: DispatchTemporary[] = [...this.temporaryList.data];

    data.forEach((newItem: DispatchTemporary) => {
      // Remove existing item if found
      updatedData = updatedData.filter(
        (item: DispatchTemporary) => item.dtSttNo !== newItem.dtSttNo
      );

      // Add new item at the beginning
      updatedData.unshift(newItem);
    });

    this.lastScanData = updatedData[0];
    this.temporaryList.data = updatedData;

    if (this.form.q || this.form.dtScanStatus?.value) {
      this.form = new ApiRequestList();
      await this.getList();
    }
  }

  // search
  onSearch(val: string): void {
    this.form.q = val;
    this.getList();
  }

  onClearSearch(): void {
    this.form.q = "";
    this.getList();
  }

  // status
  get statusList() {
    return [
      {
        name: this.$t("dispatch.allStatus"),
        value: "all"
      },
      {
        name: this.$t("dispatch.success"),
        value: "success"
      },
      {
        name: this.$t("dispatch.failed"),
        value: "failed"
      }
    ];
  }

  // ui validation
  get buttonActionError() {
    return {
      textColor: "white",
      title: this.$t("dispatch.reload"),
      iconLeft: "ics_o_reload"
    };
  }
  get isEmptyList(): boolean {
    return (
      !this.listSttSuccess?.length &&
      !this.form.q &&
      !this.form.dtScanStatus?.value?.replace(/^all$/gi, "") &&
      !this.temporaryList.loading
    );
  }
  get isErrorList(): boolean {
    return this.form.error && !this.temporaryList.loading;
  }
  get isNotFound(): boolean {
    return (
      !this.listSttSuccess?.length &&
      (this.form.q || this.form.dtScanStatus) &&
      !this.temporaryList.loading
    );
  }

  async onProcess() {
    try {
      MainAppController.showLoading();
      let payload = new ProcessUpdateDispatchRequest();

      if (this.selectedCourierShuttle) {
        payload = new ProcessUpdateDispatchRequest(
          Number(this.selectedCourierShuttle?.id),
          this.selectedCourierShuttle?.name,
          this.selectedCourierShuttle?.phone,
          Number(this.selectedCourierShuttle?.shuttleId),
          this.selectedCourierShuttle?.shuttleName,
          Number(this.selectedCourierShuttle?.hubId),
          this.selectedCourierShuttle?.hubName,
          this.selectedCourierShuttle?.cityCode
        );
      }

      const {
        data
      }: ResponsePayloadV2 = await DispatchController.processUpdateDispatch(payload);
      this.errorEntities.onClose && this.errorEntities.onClose();
      MainAppController.closeErrorMessage();
      MainAppController.closeLoading();
      MainAppController.closeMessageModal();
      MainAppController.showMessageModal(
        new ModalMessageEntities({
          image: "badge-success",
          title: this.$t("dispatch.manifestSuccess", { dispatchNo: data?.dispatchNo }),
          textSuccess: this.$t("dispatch.ok"),
          onSubmit: this.goBack
        })
      );
      removeDataLocalStorage("dispatch-summary");
    } catch (error) {
      MainAppController.closeLoading();
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          this.$t("dispatch.processFailed"),
          this.onProcess
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  selectedCourierShuttle: CategorizedItem | null = null;

  onCourierShuttleChange(item: CategorizedItem) {
    this.selectedCourierShuttle = item;
  }

  getStyledSttNo(sttNo: string) {
    const firstTwo = sttNo.substring(0,2);
    const middle = sttNo.substring(2, sttNo.length - 4);
    const lastFour = sttNo.substring(sttNo.length - 4);
    return `<div class="flex flex-row">
      <div class="font-bold text-18px">${firstTwo}</div>${middle}<div class="font-bold text-18px">${lastFour}</div>
      </div>`
  }
}
